import React, { useState } from 'react';
import { MapPin, CheckCircle2, Check } from 'lucide-react';
import { StructuredLocation } from '../../types/location';
import { DynamicLocationSearchBox } from './DynamicLocationSearchBox';

interface BookingMeetingLocationPickerProps {
  initialLocationName?: string;
  onLocationConfirmed: (location: StructuredLocation) => void;
  className?: string;
}

export const BookingMeetingLocationPicker: React.FC<BookingMeetingLocationPickerProps> = ({
  onLocationConfirmed,
  className = '',
}) => {
  const [selectedLoc, setSelectedLoc] = useState<StructuredLocation | null>(null);
  const [isConfirmed, setIsConfirmed] = useState<boolean>(false);

  const handleSelect = (loc: StructuredLocation | null) => {
    setSelectedLoc(loc);
    if (loc) {
      onLocationConfirmed(loc);
    } else {
      setIsConfirmed(false);
    }
  };

  const handleConfirm = () => {
    if (selectedLoc) {
      setIsConfirmed(true);
      onLocationConfirmed(selectedLoc);
    }
  };

  const handleReset = () => {
    setSelectedLoc(null);
    setIsConfirmed(false);
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center justify-between">
        <label className="block text-xs font-bold text-slate-700 flex items-center gap-1.5">
          <MapPin className="w-3.5 h-3.5 text-pink-600" />
          <span>Meeting Location *</span>
        </label>
        {selectedLoc && (
          <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
            <CheckCircle2 className="w-3 h-3 text-emerald-600" />
            <span>{isConfirmed ? 'Confirmed' : 'Location Selected'}</span>
          </span>
        )}
      </div>

      {!isConfirmed ? (
        <div className="space-y-2">
          {/* 1. Search Box */}
          <DynamicLocationSearchBox
            placeholder="Search meeting city, area, mall, cafe, or PIN code"
            onLocationSelect={handleSelect}
            selectedLocation={selectedLoc}
            showCoordinatesBadge={false}
            size="md"
          />

          {/* 2. Selected Location & Confirm Button */}
          {selectedLoc && (
            <div className="p-3 bg-gradient-to-r from-pink-50/80 via-white to-purple-50/80 border border-pink-200 rounded-2xl space-y-2.5 animate-in fade-in">
              <div className="flex items-start gap-2.5">
                <div className="w-7 h-7 rounded-xl bg-pink-600 text-white flex items-center justify-center shrink-0 mt-0.5 shadow-xs">
                  <MapPin className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-extrabold text-slate-900 leading-snug">
                    {selectedLoc.formatted_address}
                  </p>
                  <div className="flex flex-wrap items-center gap-2 mt-1 text-[10px] text-slate-500">
                    {selectedLoc.city && (
                      <span className="px-1.5 py-0.5 bg-slate-100 rounded text-slate-700 font-bold">
                        {selectedLoc.city}
                      </span>
                    )}
                    {selectedLoc.state && (
                      <span className="px-1.5 py-0.5 bg-slate-100 rounded text-slate-700">
                        {selectedLoc.state}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end pt-1 border-t border-pink-100">
                <button
                  type="button"
                  id="confirm-meeting-location-btn"
                  onClick={handleConfirm}
                  className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white font-extrabold text-xs rounded-xl shadow-xs transition-all flex items-center gap-1.5 cursor-pointer"
                >
                  <Check className="w-3.5 h-3.5" />
                  <span>Confirm Location</span>
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Confirmed Location Display Card */
        <div className="p-3 bg-emerald-50/70 border border-emerald-300/80 rounded-2xl flex items-center justify-between gap-3 shadow-xs">
          <div className="flex items-start gap-2.5 min-w-0">
            <div className="w-7 h-7 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0 mt-0.5 shadow-xs">
              <CheckCircle2 className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-bold text-emerald-800 uppercase tracking-wider">
                Confirmed Meeting Location
              </p>
              <p className="text-xs font-black text-slate-900 truncate">
                {selectedLoc?.formatted_address}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleReset}
            className="px-3 py-1 bg-white hover:bg-slate-100 text-slate-700 text-xs font-bold rounded-lg border border-slate-200 transition-colors shrink-0 cursor-pointer"
          >
            Change
          </button>
        </div>
      )}
    </div>
  );
};
